import type { VendorConfig } from './ids';

/**
 * Vendor loaders and the event fan-out, ported from omega-measure.js (§1.3, §1.5).
 * Loaders run only after consent; each vendor is independent and skipped when its id is
 * empty. The base snippets are the vendors' published ones, typed.
 */

type Command = (...args: unknown[]) => void;

interface MetaStub {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[];
  push: MetaStub;
  loaded: boolean;
  version: string;
}

interface TikTokQueue extends Array<unknown> {
  methods?: string[];
  setAndDefer?: (target: Record<string, unknown>, method: string) => void;
  instance?: (id: string) => unknown[];
  load?: (id: string, options?: Record<string, unknown>) => void;
  track?: Command;
  _i?: Record<string, unknown[] & { _u?: string }>;
  _t?: Record<string, number>;
  _o?: Record<string, Record<string, unknown>>;
}

interface XStub {
  (...args: unknown[]): void;
  exe?: (...args: unknown[]) => void;
  queue: unknown[];
  version: string;
}

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: Command;
    fbq?: MetaStub;
    _fbq?: MetaStub;
    ttq?: TikTokQueue;
    TiktokAnalyticsObject?: string;
    twq?: XStub;
  }
}

/** GA4 event names, mapped for Meta and TikTok (an event must mean the same thing everywhere). */
export const META_EVENTS: Readonly<Record<string, string>> = {
  page_view: 'PageView',
  lead: 'Lead',
  sign_up: 'CompleteRegistration',
  view_item: 'ViewContent',
  add_to_cart: 'AddToCart',
  begin_checkout: 'InitiateCheckout',
  purchase: 'Purchase',
};

export const TIKTOK_EVENTS: Readonly<Record<string, string>> = {
  page_view: 'Pageview',
  lead: 'SubmitForm',
  sign_up: 'CompleteRegistration',
  view_item: 'ViewContent',
  add_to_cart: 'AddToCart',
  begin_checkout: 'InitiateCheckout',
  purchase: 'CompletePayment',
};

function appendScript(src: string): void {
  const script = document.createElement('script');
  script.async = true;
  script.src = src;
  (document.head ?? document.documentElement).appendChild(script);
}

function insertBeforeFirstScript(script: HTMLScriptElement): void {
  const first = document.getElementsByTagName('script')[0];
  if (first?.parentNode) first.parentNode.insertBefore(script, first);
  else (document.head ?? document.documentElement).appendChild(script);
}

function loadGa4(id: string): void {
  const dataLayer = (window.dataLayer = window.dataLayer ?? []);
  // gtag.js reads Arguments objects from the data layer, not arrays.
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', id, { send_page_view: false });
  appendScript('https://www.googletagmanager.com/gtag/js?id=' + encodeURIComponent(id));
}

function loadMeta(id: string): void {
  if (window.fbq === undefined) {
    const stub = function fbq(...args: unknown[]) {
      if (stub.callMethod) stub.callMethod(...args);
      else stub.queue.push(args);
    } as MetaStub;
    if (window._fbq === undefined) window._fbq = stub;
    stub.push = stub;
    stub.loaded = true;
    stub.version = '2.0';
    stub.queue = [];
    window.fbq = stub;
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    insertBeforeFirstScript(script);
  }
  window.fbq('init', id);
}

function loadTikTok(id: string): void {
  const name = 'ttq';
  window.TiktokAnalyticsObject = name;
  const ttq: TikTokQueue = (window.ttq = window.ttq ?? []);
  ttq.methods = [
    'page',
    'track',
    'identify',
    'instances',
    'debug',
    'on',
    'off',
    'once',
    'ready',
    'alias',
    'group',
    'enableCookie',
    'disableCookie',
  ];
  ttq.setAndDefer = (target, method) => {
    target[method] = (...args: unknown[]) => {
      (target as unknown as unknown[]).push([method, ...args]);
    };
  };
  for (const method of ttq.methods) ttq.setAndDefer(ttq as unknown as Record<string, unknown>, method);
  ttq.instance = (instanceId) => {
    const queue = ttq._i?.[instanceId] ?? [];
    for (const method of ttq.methods ?? []) ttq.setAndDefer?.(queue as unknown as Record<string, unknown>, method);
    return queue;
  };
  ttq.load = (sdkId, options) => {
    const base = 'https://analytics.tiktok.com/i18n/pixel/events.js';
    ttq._i = ttq._i ?? {};
    const instance: unknown[] & { _u?: string } = [];
    instance._u = base;
    ttq._i[sdkId] = instance;
    ttq._t = ttq._t ?? {};
    ttq._t[sdkId] = Date.now();
    ttq._o = ttq._o ?? {};
    ttq._o[sdkId] = options ?? {};
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = base + '?sdkid=' + sdkId + '&lib=' + name;
    insertBeforeFirstScript(script);
  };
  ttq.load(id);
}

function loadX(id: string): void {
  if (window.twq === undefined) {
    const stub = function twq(...args: unknown[]) {
      if (stub.exe) stub.exe(...args);
      else stub.queue.push(args);
    } as XStub;
    stub.version = '1.1';
    stub.queue = [];
    window.twq = stub;
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://static.ads-twitter.com/uwt.js';
    insertBeforeFirstScript(script);
  }
  window.twq('config', id);
}

/** Loads every armed vendor, in the order GA4 → Meta → TikTok → X. Call at most once per document. */
export function loadVendors(config: VendorConfig): void {
  if (config.ga4) loadGa4(config.ga4);
  if (config.meta) loadMeta(config.meta);
  if (config.tiktok) loadTikTok(config.tiktok);
  if (config.x) loadX(config.x);
}

let sendFailureReported = false;

/** Sends one event to every loaded vendor. Never throws into the page. */
export function sendEvent(config: VendorConfig, name: string, params: Readonly<Record<string, unknown>>): void {
  try {
    if (config.ga4 && window.gtag) window.gtag('event', name, params);
    if (config.meta && window.fbq) {
      const mapped = META_EVENTS[name];
      if (mapped) window.fbq('track', mapped, params);
      else window.fbq('trackCustom', name, params);
    }
    // X takes a conversion EVENT id issued by Ads Manager, never a free-text name.
    if (config.x && window.twq && name === 'purchase' && config.xPurchaseEvent) {
      window.twq('event', config.xPurchaseEvent, {
        value: params.value,
        currency: params.currency,
        conversion_id: params.transaction_id || params.order_id,
      });
    }
    if (config.tiktok && window.ttq?.track) {
      const mapped = TIKTOK_EVENTS[name];
      if (mapped) window.ttq.track(mapped, params);
    }
  } catch (error) {
    if (!sendFailureReported) {
      sendFailureReported = true;
      console.warn('measurement: a vendor rejected an event; later failures are not reported', name, error);
    }
  }
}
